import { useEffect } from "react";

/**
 * Custom hook to fix tab navigation order for Kendo Grid filter inputs.
 * Ensures that tabbing through filter inputs follows left-to-right column order
 * instead of the default DOM order.
 */
export const useGridFilterTabNavigation = () => {
	useEffect(() => {
		const findFilterInputs = (): HTMLInputElement[] => {
			const selectors = [
				".k-grid .k-filtercell input",
				".k-grid .k-filter-row input",
				'.k-grid input[data-role="textbox"]',
				".k-grid .k-textbox",
			];

			for (const selector of selectors) {
				const inputs = [
					...document.querySelectorAll(selector),
				] as HTMLInputElement[];
				if (inputs.length > 0) {
					return inputs;
				}
			}

			return [];
		};

		const focusFirstDataCell = () => {
			const firstDataCell = document.querySelector(
				".k-grid .k-table-tbody .k-table-td:first-child",
			);
			if (firstDataCell) {
				(firstDataCell as HTMLElement).focus();
			}
		};

		const focusLastDataCell = () => {
			const lastDataCell = document.querySelector(
				".k-grid .k-table-tbody .k-table-tr:last-child .k-table-td:last-child",
			);
			if (lastDataCell) {
				(lastDataCell as HTMLElement).focus();
			}
		};

		const focusNextFilter = (
			filterInputs: HTMLInputElement[],
			currentIndex: number,
		) => {
			const nextInput = filterInputs[currentIndex + 1];
			if (nextInput) {
				nextInput.focus();
			} else {
				focusFirstDataCell();
			}
		};

		const focusPreviousFilter = (
			filterInputs: HTMLInputElement[],
			currentIndex: number,
		) => {
			const previousInput =
				currentIndex - 1 >= 0 ? filterInputs[currentIndex - 1] : undefined;
			if (previousInput) {
				previousInput.focus();
			} else {
				focusLastDataCell();
			}
		};

		const handleKeyDown = (e: KeyboardEvent) => {
			if (e.key !== "Tab") {
				return;
			}

			const target = e.target as HTMLElement;

			// Check if we're in a filter input
			if (
				!target?.matches(
					".k-grid input, .k-grid .k-textbox, .k-filtercell input",
				)
			) {
				return;
			}

			const filterInputs = findFilterInputs();
			if (filterInputs.length === 0) {
				return;
			}

			e.preventDefault();

			const currentIndex = filterInputs.indexOf(target as HTMLInputElement);

			if (e.shiftKey) {
				focusPreviousFilter(filterInputs, currentIndex);
			} else {
				focusNextFilter(filterInputs, currentIndex);
			}
		};

		// Add keyboard event listener to the document
		document.addEventListener("keydown", handleKeyDown);

		const fixTabOrder = () => {
			const filterInputs = findFilterInputs();

			if (filterInputs.length === 0) {
				return;
			}

			// Map column positions to proper tab indices.
			// IMPORTANT: only write when the value actually differs. Setting an
			// attribute is itself a DOM mutation — without this idempotency
			// check, the MutationObserver below would feed back into this
			// function in a loop.
			for (const [index, input] of filterInputs.entries()) {
				const tabIndex = index + 1;
				if (input.tabIndex !== tabIndex) {
					input.tabIndex = tabIndex;
				}
			}
		};

		// Run immediately for any existing grid
		fixTabOrder();

		// Also run after multiple delays to catch grids that render asynchronously
		const timeoutIds = [
			setTimeout(fixTabOrder, 100),
			setTimeout(fixTabOrder, 500),
			setTimeout(fixTabOrder, 1000),
		];

		// Coalesce bursts of mutations (e.g. virtual-scroll row mount/unmount,
		// sort, column reorder) into a single rAF-batched pass so we don't run
		// a full-document scan per mutation.
		let rafId = 0;
		const scheduleFix = () => {
			if (rafId) return;
			rafId = requestAnimationFrame(() => {
				rafId = 0;
				fixTabOrder();
			});
		};

		// Only react to mutations that actually add/remove element nodes that
		// could be (or contain) filter inputs. The old implementation fired on
		// every text/attribute change anywhere in `document.body` — which was
		// a major perf hog when rows mount/unmount during sort/scroll.
		const observer = new MutationObserver((mutations) => {
			for (const m of mutations) {
				if (m.addedNodes.length === 0 && m.removedNodes.length === 0) {
					continue;
				}
				const nodes = [...m.addedNodes, ...m.removedNodes];
				for (const node of nodes) {
					if (node.nodeType !== Node.ELEMENT_NODE) continue;
					const el = node as Element;
					if (
						el.matches?.(".k-filtercell, .k-filter-row, .k-grid") ||
						el.querySelector?.(".k-filtercell, .k-filter-row")
					) {
						scheduleFix();
						return;
					}
				}
			}
		});

		// Scope the observer to existing `.k-grid` elements rather than the
		// whole document. Falls back to `document.body` if no grids are mounted
		// yet (so a grid mounted later still gets picked up — its mount will
		// add `.k-grid` nodes that trigger the fix).
		const grids = document.querySelectorAll(".k-grid");
		const observeTargets =
			grids.length > 0 ? [...grids] : [document.body as Element];
		for (const target of observeTargets) {
			observer.observe(target, { childList: true, subtree: true });
		}

		return () => {
			document.removeEventListener("keydown", handleKeyDown);
			for (const timeoutId of timeoutIds) {
				clearTimeout(timeoutId);
			}
			if (rafId) cancelAnimationFrame(rafId);
			observer.disconnect();
		};
	}, []);
};
